import { Maximize2, MoreVertical } from "lucide-react";
import { motion } from "motion/react";
import { Streamdown } from "streamdown";
import { Button } from "@/components/ui/button";
import type { CanvasCard, ResizeDirection } from "./canvas-utils";

interface CanvasCardItemProps {
	card: CanvasCard;
	/** Position in the list — staggers the enter animation. */
	index: number;
	isCommentMode: boolean;
	onDragStart: (e: React.MouseEvent, card: CanvasCard) => void;
	onResizeStart: (
		e: React.MouseEvent,
		card: CanvasCard,
		direction: ResizeDirection,
	) => void;
	onOpenEditor: (card: CanvasCard) => void;
}

/** A draggable, resizable tool-result card on the canvas. */
export function CanvasCardItem({
	card,
	index: i,
	isCommentMode,
	onDragStart: handleCardDragStart,
	onResizeStart: handleResizeStart,
	onOpenEditor,
}: CanvasCardItemProps) {
	return (
		<div
			className="absolute group/card"
			style={{
				left: card.x,
				top: card.y,
				width: card.width,
				height: card.height,
				pointerEvents: isCommentMode ? "none" : "auto",
			}}
		>
			<motion.div
				className="w-full h-full"
				initial={{ opacity: 0, y: 20, scale: 0.95 }}
				animate={{ opacity: 1, y: 0, scale: 1 }}
				transition={{ duration: 0.4, delay: 0.2 + i * 0.1 }}
			>
				<div className="bg-bg-surface rounded-2xl shadow-popover overflow-hidden w-full h-full flex flex-col">
					{/* Card Header — drag handle */}
					<div
						className="flex items-center justify-between px-5 py-3 border-b border-border-subtle cursor-move flex-shrink-0"
						onMouseDown={(e) => handleCardDragStart(e, card)}
					>
						<div className="flex items-center gap-2">
							<span className="text-sm">{card.icon}</span>
							<span className="text-xl font-medium text-text-primary">
								{card.title}
							</span>
						</div>
						<div
							className="flex items-center gap-1"
							onMouseDown={(e) => e.stopPropagation()}
						>
							<Button
								variant="ghost"
								size="sm"
								className="gap-1.5"
								onClick={() => onOpenEditor(card)}
							>
								<Maximize2 size={12} />
								Open editor
							</Button>
							<Button variant="ghost" size="icon-xs">
								<MoreVertical size={14} />
							</Button>
						</div>
					</div>

					{/* Card Content */}
					<div
						data-card-content
						className="relative px-5 py-4 overflow-hidden cursor-auto bg-bg-canvas flex-1"
						onMouseDown={(e) => e.stopPropagation()}
					>
						<div
							className="text-sm text-text-secondary leading-relaxed"
							style={{
								overflowWrap: "break-word",
								wordBreak: "break-word",
							}}
						>
							<Streamdown>{card.content}</Streamdown>
						</div>

						{/* Fade bottom */}
						<div className="pointer-events-none absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-bg-canvas to-transparent" />
					</div>

					{/* Card Footer */}
					<div
						className="px-5 py-3 flex justify-center bg-bg-canvas flex-shrink-0"
						onMouseDown={(e) => e.stopPropagation()}
					>
						<Button
							variant="outline"
							size="sm"
							className="gap-1.5"
							onClick={() => onOpenEditor(card)}
						>
							<Maximize2 size={12} />
							Open editor
						</Button>
					</div>
				</div>

				{/* ── Resize Handles ── */}
				{/* Edge handles */}
				<div
					className="absolute top-0 left-2 right-2 h-1.5 cursor-n-resize opacity-0 group-hover/card:opacity-100 transition-opacity"
					onMouseDown={(e) => handleResizeStart(e, card, "n")}
				/>
				<div
					className="absolute bottom-0 left-2 right-2 h-1.5 cursor-s-resize opacity-0 group-hover/card:opacity-100 transition-opacity"
					onMouseDown={(e) => handleResizeStart(e, card, "s")}
				/>
				<div
					className="absolute left-0 top-2 bottom-2 w-1.5 cursor-w-resize opacity-0 group-hover/card:opacity-100 transition-opacity"
					onMouseDown={(e) => handleResizeStart(e, card, "w")}
				/>
				<div
					className="absolute right-0 top-2 bottom-2 w-1.5 cursor-e-resize opacity-0 group-hover/card:opacity-100 transition-opacity"
					onMouseDown={(e) => handleResizeStart(e, card, "e")}
				/>
				{/* Corner handles */}
				<div
					className="absolute -top-1 -left-1 w-3 h-3 cursor-nw-resize z-10"
					onMouseDown={(e) => handleResizeStart(e, card, "nw")}
				/>
				<div
					className="absolute -top-1 -right-1 w-3 h-3 cursor-ne-resize z-10"
					onMouseDown={(e) => handleResizeStart(e, card, "ne")}
				/>
				<div
					className="absolute -bottom-1 -left-1 w-3 h-3 cursor-sw-resize z-10"
					onMouseDown={(e) => handleResizeStart(e, card, "sw")}
				/>
				<div
					className="absolute -bottom-1 -right-1 w-3 h-3 cursor-se-resize z-10"
					onMouseDown={(e) => handleResizeStart(e, card, "se")}
				/>
			</motion.div>
		</div>
	);
}
